<template>
  <div v-if="showActionsBanner">
    <el-card>
      <el-header>
        <el-row>
          <el-col :span="23">
            <div class="banner-title">
              <icon glyph="warning" />
              <span>{{ $t('Dashboard_Required_Actions_Banner_Title') }}</span>
            </div>
          </el-col>
          <el-col :span="1">
            <el-icon name="close" class="close-btn" @click.native="close()" />
          </el-col>
        </el-row>
      </el-header>
      <template v-if="showTagLimitWarning">
        <el-row class="ga-section">
          <el-col>
            <div class="expired p-bottom-10">
              <icon glyph="warning" />
              {{ getTagLimitWarningTitle() }}
            </div>
            <div class="p-bottom-10">
              <div>
                {{ getTagLimitWarningDescription() }}
              </div>
            </div>
            <router-link :to="tagsManagerRoute">
              <el-button type="primary">
                {{
                  $t('Dashboard_Required_Actions_Banner_Btn_GoToTagsManager')
                }}
                <i class="el-icon-right" />
              </el-button>
            </router-link>
          </el-col>
        </el-row>
      </template>
      <template v-if="isAndroidLegacyAuthenticationUsed">
        <div class="separator" v-if="showTagLimitWarning"></div>
        <el-row class="ga-section">
          <el-col>
            <div class="expired p-bottom-10">
              <icon glyph="warning" />
              {{ $t('Dashboard_Required_Actions_AndroidLegacy_Title') }}
            </div>
            <div class="p-bottom-10">
              <div>
                {{ $t('Dashboard_Required_Actions_AndroidLegacy_Descr') }}
                <a
                  href="https://docs.xtremepush.com/docs/android-push-notifications"
                  target="_blank"
                >
                  {{ $t('ApplicationsChannels_AndroidLegacyNotice_LinkText') }}
                </a>
              </div>
            </div>
            <router-link :to="appsAndSitesRoute">
              <el-button type="primary">
                {{ $t('Dashboard_Required_Actions_Banner_Btn_GoToApps') }}
                <i class="el-icon-right" />
              </el-button>
            </router-link>
          </el-col>
        </el-row>
      </template>
      <template v-if="showOptimoveWithUsername">
        <div
          class="separator"
          v-if="showTagLimitWarning || isAndroidLegacyAuthenticationUsed"
        ></div>
        <el-row class="ga-section">
          <el-col>
            <div class="expired p-bottom-10">
              <icon glyph="warning" />
              {{ $t('Dashboard_Required_Actions_Optimove_Title') }}
            </div>
            <div class="p-bottom-10">
              <div>{{ $t('Dashboard_Required_Actions_Optimove_Descr') }}</div>
            </div>
            <router-link :to="optimoveIntegrationsRoute">
              <el-button type="primary">
                {{
                  $t('Dashboard_Required_Actions_Optimove_Btn_GoToIntegrations')
                }}
                <i class="el-icon-right" />
              </el-button>
            </router-link>
          </el-col>
        </el-row>
      </template>
      <template v-if="brokenAudienceIntegrations.length">
        <div class="separator" v-if="showOptimoveWithUsername"></div>
        <el-row>
          <el-col :span="24">
            <el-table
              :data="brokenAudienceIntegrations"
              border
              :show-header="false"
              cell-class-name="cell"
            >
              <el-table-column width="320">
                <template slot-scope="scope">
                  <div>
                    {{ scope.row.name }}
                    <el-tag
                      effect="plain"
                      size="mini"
                      type="success"
                      class="channel-type-badge"
                    >
                      {{ $t(labels[scope.row.type]) }}
                    </el-tag>
                  </div>
                </template>
              </el-table-column>
              <el-table-column width="650">
                <template>
                  <span class="expired">
                    <icon glyph="warning" />
                    {{
                      $t('Dashboard_Required_Actions_BrokenIntegration_Desc')
                    }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column width="50">
                <template slot-scope="scope">
                  <el-button
                    v-if="$can('viewProjectTechnical')"
                    class="navigate-app"
                    size="mini"
                    icon="el-icon-right"
                    @click="openIntegrationDetails(scope.row.id)"
                  />
                </template>
              </el-table-column>
            </el-table>
          </el-col>
          <el-col :span="9"></el-col>
        </el-row>
      </template>
      <template v-if="actions.length">
        <div
          class="separator"
          v-if="
            showTagLimitWarning ||
              isAndroidLegacyAuthenticationUsed ||
              showOptimoveWithUsername
          "
        />
        <el-row>
          <el-col :span="24">
            <el-table
              :data="actions"
              border
              :show-header="false"
              cell-class-name="cell"
            >
              <el-table-column width="320">
                <template slot-scope="scope">
                  <div>
                    {{ scope.row.name }}
                    <el-tag
                      effect="plain"
                      size="mini"
                      type="success"
                      class="channel-type-badge"
                    >
                      {{ scope.row.certificate_type }}
                    </el-tag>
                  </div>
                </template>
              </el-table-column>
              <el-table-column width="650">
                <template slot-scope="scope">
                  <span
                    v-if="
                      scope.row.certificateExpirationTimestamp * 1000 <
                        now.format('x')
                    "
                    class="expired"
                  >
                    <icon glyph="warning" />
                    {{
                      $t('Dashboard_Required_Actions_Banner_Expired_Warning', {
                        app_type: scope.row.type
                      })
                    }}
                  </span>
                  <span v-else class="expiring">
                    <el-icon name="time"></el-icon>
                    <span v-if="getExpirationRemainingDays(scope.row) >= 1">
                      {{
                        $t(
                          'Dashboard_Required_Actions_Banner_Expiring_Warning',
                          {
                            app_type: scope.row.type,
                            days_remaining: getExpirationRemainingDays(
                              scope.row
                            )
                          }
                        )
                      }}
                    </span>
                    <span v-else-if="getExpirationRemainingDays(scope.row) < 1">
                      {{
                        $t(
                          'Dashboard_Required_Actions_Banner_Expiring_Warning_Less',
                          {
                            app_type: scope.row.type
                          }
                        )
                      }}
                    </span>
                  </span>
                </template>
              </el-table-column>
              <el-table-column width="50">
                <template slot-scope="scope">
                  <el-button
                    v-if="
                      ($can('manageProjectTechnical') ||
                        $can('viewProjectTechnical')) &&
                        $can('createProjectItem')
                    "
                    class="navigate-app"
                    size="mini"
                    icon="el-icon-right"
                    @click="openDetails(scope.row)"
                  />
                </template>
              </el-table-column>
            </el-table>
          </el-col>
          <el-col :span="9"></el-col>
        </el-row>
        <el-row class="bottom-actions">
          <el-col>
            <router-link :to="channelsRoute">
              <el-button
                type="primary"
                v-if="
                  this.$can('manageProjectTechnical') ||
                    this.$can('viewProjectTechnical')
                "
              >
                {{ $t('Dashboard_Required_Actions_Banner_Channels_Btn') }}
                <i class="el-icon-right" />
              </el-button>
            </router-link>
          </el-col>
        </el-row>
      </template>
    </el-card>
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { $hasFeature } from '@/core/utils/VueAcl'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import moment from 'moment'

export default {
  name: 'RequiredActionsBanner',
  data() {
    return {
      closeCountLimit: 3,
      actions: [],
      brokenAudienceIntegrations: [],
      closed: false,
      userConfig: null,
      labels: {
        googleads: 'Integrations_GoogleAds',
        meta: 'Integrations_Meta'
      },
      moment
    }
  },
  mounted() {
    let sessionValue = window.localStorage.getItem(
      `cert_expiration_banner_closed`
    )
    this.userConfig = this.$store.getters.userConfig
    if (
      this.userConfig.cert_expiration_banner_close_count <=
        this.closeCountLimit &&
      !sessionValue
    ) {
      this.fetchRequiredActions()
    }
  },
  computed: {
    showOptimoveWithUsername() {
      return this.$store.state.app.integrations.optimoveWithUsername
    },
    isAndroidLegacyAuthenticationUsed() {
      return $hasFeature('isLegacyAndroidAuthentication')
    },
    showTagLimitWarning() {
      return this.$store.state.app.project.tag_limit_status > 0
    },
    showActionsBanner() {
      return (
        (this.actions.length ||
          this.brokenAudienceIntegrations.length ||
          this.showOptimoveWithUsername ||
          this.isAndroidLegacyAuthenticationUsed ||
          this.showTagLimitWarning) &&
        !this.closed
      )
    },
    now() {
      return moment()
    },
    channelsRoute() {
      return projectRoute({
        name: 'EnableChannelsTab'
      })
    },
    tagsManagerRoute() {
      return projectRoute({
        name: 'DataManagerAttributesTags'
      })
    },
    appsAndSitesRoute() {
      return projectRoute({
        name: 'Applications'
      })
    },
    optimoveIntegrationsRoute() {
      return projectRoute({
        name: 'IntegrationsList',
        query: {
          type: 'optimove'
        }
      })
    }
  },
  methods: {
    getTagLimitWarningTitle() {
      let translation = ''
      if (this.$store.state.app.project.tag_limit_status === 1) {
        translation = 'Dashboard_Required_Actions_Tag_Limit_Approaching'
      } else if (this.$store.state.app.project.tag_limit_status === 2) {
        translation = 'Dashboard_Required_Actions_Tag_Limit_Reached'
      }

      return this.$t(translation)
    },
    getTagLimitWarningDescription() {
      let translation = ''
      if (this.$store.state.app.project.tag_limit_status === 1) {
        translation = 'Dashboard_Required_Actions_Tag_Limit_Approaching_Desc'
      } else if (this.$store.state.app.project.tag_limit_status === 2) {
        translation = 'Dashboard_Required_Actions_Tag_Limit_Reached_Desc'
      }

      return this.$t(translation, {
        limit: this.$store.state.app.project.tag_limit
      })
    },
    close() {
      window.localStorage.setItem(`cert_expiration_banner_closed`, 1)
      this.$store.dispatch('saveUserPreferences', {
        cert_expiration_banner_close_count:
          this.userConfig.cert_expiration_banner_close_count + 1
      })

      this.closed = true
    },
    getExpirationRemainingDays(row) {
      return Math.abs(
        this.now.diff(row.certificateExpirationTimestamp * 1000, 'days')
      )
    },
    openIntegrationDetails(id) {
      this.$router.push(
        projectRoute({
          name: 'Integration',
          query: { id: id }
        })
      )
    },
    openDetails(app) {
      this.$router.push(
        projectRoute({
          name: 'ApplicationPush',
          query: { id: app.id }
        })
      )
    },
    fetchRequiredActions() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'dashboard/required-actions',
          params: {}
        })
      ).then(({ data }) => {
        this.actions = data.expired
        this.brokenAudienceIntegrations = data.brokenAudienceIntegrations
      })
    }
  }
}
</script>

<style scoped>
.close-btn {
  float: right;
  font-size: 1.7em;
  cursor: pointer;
}

.el-card {
  color: #666;
}

.banner-title {
  display: flex;
}

.banner-title span {
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
}

.banner-title .icon {
  font-size: 1.5em;
  margin-right: 10px;
}

.el-table {
  border-radius: 7px;
  border: 1px solid #ebeef5;
}

>>> .el-table td {
  padding: 6px 0 !important;
}

>>> .cell {
  border-right: none !important;
}

.expired {
  color: red;
}

.ga-section {
  font-size: 14px;
}
.ga-section a {
  color: #1d87c8;
}
.ga-section a:hover {
  color: #104b6f;
}

.separator {
  margin: 20px -20px;
  border-top: 1px solid #dbdee7;
}

.el-button--mini {
  padding: 6px 6px;
}

.el-row {
  margin-bottom: 20px;
}
.navigate-app {
  cursor: pointer;
}
.el-header {
  padding: 0;
  height: 40px !important;
}
.bottom-actions {
  margin-bottom: 0;
}
.channel-type-badge {
  color: #7e8596;
  border-color: #7e8596;
}
</style>



// WEBPACK FOOTER //
// src/modules/dashboard/components/RequiredActionsBanner.vue